import React from 'react';
import { ActionUiPresenter, DocumentRestriction, DocumentStatus, Uploader } from 'types/documentsService';
import { downloadByUrl } from '@insurancemenu/imenu-components-lib/dist/helpers/downloadByUrl';
import { observer } from 'mobx-react-lite';
import { colors, OverlaySpinner } from '@insurancemenu/imenu-components-lib';
import { RequestSignatureModal } from 'components/common/documents-upload/RequestSignatureModal';
import { RequestSignatureModalStore } from 'store/modules/upload-documents/ui-stores/requestSignatureModalStore';
import { EditDocumentModalStore } from 'store/modules/upload-documents/ui-stores/editDocumentModalStore';
import EditDocumentModal from 'components/common/documents-upload/EditDocumentModal';
import { normDateToAmericanDateTime } from '@insurancemenu/imenu-components-lib/dist/helpers/dateFormatters';
import DocumentTag from 'components/common/documents-upload/DocumentTag';
import DocumentStatusIcon from 'components/common/documents-upload/DocumentStatusIcon';
import { EditTemplateModalStore } from 'store/modules/upload-documents/ui-stores/editTemplateModalStore';
import {
  CardHeaderStyled,
  CardInfoHeadingStyled,
  CardStyled,
  CardTitleStyled,
  CardBodyStyled,
  DescriptionTextStyled,
  DownloadTemplateButtonStyled,
  TopSectionStyled,
  UploadedTextStyled,
  CardTitleWrapperStyled,
  RestrictionLabelStyled,
} from './styles';
import { AvailableActions } from '../AvailableActions';
import WarningModals from '../WarningModals';
import { DOCUMENT_CARD_SPINNER_TEST_ID, DOCUMENT_CARD_TEST_ID, DOWNLOAD_TEMPLATE_BUTTON_TEST_ID } from '../constants';
import EditTemplateModal from '../EditTemplateModal';

interface DocumentCardProps {
  isLoading: boolean;
  title: string;
  status: DocumentStatus;
  availableActions: ActionUiPresenter[];
  uploader: Uploader | null;
  description: string;
  passedRestriction: boolean;
  warningModals: WarningModals;
  restriction?: DocumentRestriction;
  disableMutativeActions?: boolean;
  downloadUrl?: string;
  requestSignatureModalStore?: RequestSignatureModalStore;
  editDocumentModalStore?: EditDocumentModalStore;
  editTemplateModalStore?: EditTemplateModalStore;
}

function getRestrictionLabel(passedRestriction: boolean, restriction?: DocumentRestriction): React.ReactNode {
  if (restriction === DocumentRestriction.BYPASS_ALLOWED) {
    return <RestrictionLabelStyled>(Optional)</RestrictionLabelStyled>;
  }
  if (!passedRestriction) {
    if (restriction === DocumentRestriction.APPROVE_REQUIRED) {
      return <RestrictionLabelStyled $color={colors.red[600]}>(Approve Required)</RestrictionLabelStyled>;
    }
    if (restriction === DocumentRestriction.UPLOAD_REQUIRED) {
      return <RestrictionLabelStyled $color={colors.red[600]}>(Upload Required)</RestrictionLabelStyled>;
    }
  }

  return null;
}

export const DocumentCard: React.FC<DocumentCardProps> = observer(
  ({
    isLoading,
    title,
    restriction,
    passedRestriction,
    status,
    availableActions,
    warningModals,
    uploader,
    downloadUrl,
    description,
    requestSignatureModalStore,
    editDocumentModalStore,
    editTemplateModalStore,
    disableMutativeActions = false,
  }) => {
    function onDownloadTemplate(): void {
      if (!downloadUrl) {
        throw new Error(`Invalid download url! ${downloadUrl}`);
      }
      downloadByUrl(downloadUrl);
    }

    return (
      <>
        <CardStyled $status={status} data-test-id={DOCUMENT_CARD_TEST_ID}>
          <CardHeaderStyled>
            <CardTitleWrapperStyled>
              <CardTitleStyled>
                <DocumentStatusIcon status={status} />
                {title}
              </CardTitleStyled>
              {getRestrictionLabel(passedRestriction, restriction)}
            </CardTitleWrapperStyled>
            <DocumentTag status={status} />
          </CardHeaderStyled>
          <CardBodyStyled>
            <TopSectionStyled>
              <div>
                <CardInfoHeadingStyled>Uploaded By</CardInfoHeadingStyled>
                <UploadedTextStyled>
                  {uploader
                    ? `${uploader?.name}, ${normDateToAmericanDateTime(uploader?.creationDateTime)}`
                    : '--Not Uploaded--'}
                </UploadedTextStyled>
              </div>
              <AvailableActions
                availableActions={availableActions}
                disableMutativeActions={disableMutativeActions}
                isLoading={isLoading}
                warningModals={warningModals}
              />
            </TopSectionStyled>
            {description ? (
              <>
                <CardInfoHeadingStyled>Description</CardInfoHeadingStyled>
                <div>
                  <DescriptionTextStyled>{description}</DescriptionTextStyled>
                </div>
              </>
            ) : null}
            {downloadUrl ? (
              <DownloadTemplateButtonStyled
                type="text"
                color="primary"
                size="small"
                onClick={onDownloadTemplate}
                data-test-id={DOWNLOAD_TEMPLATE_BUTTON_TEST_ID}
              >
                Download Template
              </DownloadTemplateButtonStyled>
            ) : null}
          </CardBodyStyled>
          <OverlaySpinner display={isLoading} data-test-id={DOCUMENT_CARD_SPINNER_TEST_ID} />
        </CardStyled>
        {requestSignatureModalStore && <RequestSignatureModal modalStore={requestSignatureModalStore} />}
        {editDocumentModalStore && <EditDocumentModal modalStore={editDocumentModalStore} />}
        {editTemplateModalStore && <EditTemplateModal modalStore={editTemplateModalStore} />}
        <WarningModals availableActions={availableActions} warningModals={warningModals} />
      </>
    );
  },
);
